// Frontend types matching backend

export interface Story {
  id: string;
  sessionId: string;
  title: string;
  theme: string;
  provider: 'nim' | 'openai' | 'ollama';
  model?: string;
  createdAt: number;
  updatedAt: number;
  lastAccessedAt: number;
  status: 'active' | 'completed' | 'abandoned';
}

export interface Session {
  id: string;
  storyId: string;
  startedAt: number;
  endedAt?: number;
}

export interface Character {
  id: string;
  sessionId: string;
  name: string;
  class: CharacterClass;
  stats: CharacterStats;
  hp: number;
  maxHp: number;
  level: number;
  xp: number;
  inventory: Item[];
}

export type CharacterClass = 'warrior' | 'mage' | 'rogue' | 'cleric';

export interface CharacterStats {
  str: number;
  dex: number;
  con: number;
  int: number;
  wis: number;
  cha: number;
}

export interface Enemy {
  name: string;
  hp: number;
  maxHp: number;
  weapon: string;
  class: string;
}

export interface CombatEncounter {
  id: string;
  sessionId: string;
  enemies: Enemy[];
  createdAt: number;
}

export type ItemType = 'consumable' | 'weapon' | 'armor' | 'quest' | 'misc';
export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export interface Item {
  id: string;
  sessionId: string;
  name: string;
  quantity: number;
  type?: ItemType;
  healAmount?: number;
  rarity?: ItemRarity;
}

export interface Scene {
  id: string;
  sessionId: string;
  title?: string;
  location?: string;
  text: string;
  choiceMade?: string;
  rollResult?: number;
  rollType?: string;
  damage: number;
  heal: number;
  damageReason?: string;
  loot?: string;
  choicesJson?: string;
  createdAt: number;
}

export interface Choice {
  id: string;
  description: string;
  needsRoll: boolean;
}

export interface GameState {
  story: Story;
  character: Character;
  sceneHistory: Scene[];
  currentScene?: Scene;
  choices: Choice[];
}

export interface CreateStoryRequest {
  theme: string;
  characterName: string;
  characterClass: CharacterClass;
  provider?: 'nim' | 'openai' | 'ollama';
  token?: string;
}

export interface CreateStoryResponse {
  sessionId: string;
  story: Story;
  session: Session;
  character: Character;
  scene: Scene;
  choices: Choice[];
}

export interface NextSceneResponse {
  scene: Scene;
  choices: Choice[];
  character: Character;
  leveledUp?: boolean;
  previousLevel?: number;
}

export type PromptType = 'opening' | 'continue' | 'death' | 'memory_summarize' | 'memory_extract';

export interface PromptEntry {
  id: string;
  sessionId: string;
  sceneIndex: number;
  promptType: PromptType;
  systemPrompt: string;
  userPrompt: string;
  response: string;
  createdAt: number;
}

export interface RollDiceResponse {
  roll: RollResult;
  statInfo?: {
    statName: string;
    statAbbrev: string;
    abilityModifier: number;
    proficiencyBonus: number;
    hasProficiency: boolean;
  };
}

export interface RollDiceRequest {
  diceType: DiceType;
  modifier?: number;
  actionType?: string;
  advantage?: 'normal' | 'advantage' | 'disadvantage';
}

export interface RollResult {
  diceType: DiceType;
  rolls: number[];
  modifier: number;
  total: number;
  isAdvantage?: boolean;
  isDisadvantage?: boolean;
}

export type DiceType = 'd20' | 'd12' | 'd10' | 'd8' | 'd6' | 'd4';

export type MemoryType = 'scene' | 'event' | 'choice' | 'npc' | 'location' | 'summary';

export interface Memory {
  id: string;
  sessionId: string;
  content: string;
  type: MemoryType;
  tags?: string[];
  embedding?: number[];
  importance: number;
  createdAt: number;
}

export interface EmbeddingOptions {
  provider: 'nim' | 'openai' | 'ollama';
  apiKey?: string;
  baseUrl?: string;
  model?: string;
}

export const CHARACTER_CLASSES: Record<CharacterClass, { 
  name: string; 
  stats: CharacterStats & { hp: number; maxHp: number };
  description: string;
  icon: string;
}> = {
  warrior: {
    name: 'Warrior',
    stats: { str: 16, dex: 14, con: 14, int: 10, wis: 12, cha: 10, hp: 24, maxHp: 24 },
    description: 'A skilled fighter with strength and endurance.',
    icon: '⚔️'
  },
  mage: {
    name: 'Mage',
    stats: { str: 8, dex: 12, con: 10, int: 16, wis: 14, cha: 12, hp: 16, maxHp: 16 },
    description: 'A master of arcane arts and spellcasting.',
    icon: '🔮'
  },
  rogue: {
    name: 'Rogue',
    stats: { str: 10, dex: 16, con: 12, int: 12, wis: 12, cha: 14, hp: 18, maxHp: 18 },
    description: 'A cunning trickster skilled in stealth and deception.',
    icon: '🗡️'
  },
  cleric: {
    name: 'Cleric',
    stats: { str: 14, dex: 10, con: 14, int: 10, wis: 16, cha: 12, hp: 20, maxHp: 20 },
    description: 'A divine healer blessed by the gods.',
    icon: '✧'
  }
};

export function getStatModifier(stat: number): number {
  return Math.floor((stat - 10) / 2);
}

export function formatModifier(mod: number): string {
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

export const XP_THRESHOLDS = [
  0,      // Level 1
  300,    // Level 2
  900,    // Level 3
  2700,   // Level 4
  6500,   // Level 5
  14000,  // Level 6
  23000,  // Level 7
  34000,  // Level 8
  48000,  // Level 9
  64000,  // Level 10
  85000,  // Level 11
  100000, // Level 12
  120000, // Level 13
  140000, // Level 14
  165000, // Level 15
  195000, // Level 16
  225000, // Level 17
  265000, // Level 18
  305000, // Level 19
  355000  // Level 20
];

export function getProficiencyBonus(level: number): number {
  if (level < 5) return 2;
  if (level < 9) return 3;
  if (level < 13) return 4;
  if (level < 17) return 5;
  return 6;
}

export function getXpForNextLevel(level: number): number {
  if (level >= 20) return XP_THRESHOLDS[19];
  return XP_THRESHOLDS[level];
}

export function getXpForCurrentLevel(level: number): number {
  if (level <= 1) return 0;
  return XP_THRESHOLDS[level - 1];
}

export function getLevelFromXp(xp: number): number {
  for (let i = XP_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= XP_THRESHOLDS[i]) {
      return i + 1;
    }
  }
  return 1;
}

export function getXpProgress(xp: number): { current: number; needed: number; percentage: number } {
  const level = getLevelFromXp(xp);
  if (level >= 20) {
    return { current: 0, needed: 0, percentage: 100 };
  }
  const currentLevelXp = getXpForCurrentLevel(level);
  const nextLevelXp = getXpForNextLevel(level);
  const needed = nextLevelXp - currentLevelXp;
  const current = xp - currentLevelXp;
  return {
    current,
    needed,
    percentage: Math.min(100, (current / needed) * 100)
  };
}
